import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button, Tag, Space, Modal, Select, Input, Divider, Spin, Alert,
  Segmented, InputNumber, Switch, Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  CheckCircleOutlined, CrownOutlined, CreditCardOutlined, CloseOutlined,
  InfoCircleOutlined, ReloadOutlined,
} from "@ant-design/icons";
import {
  fetchActivePlans, fetchCurrentSubscription, fetchPaymentMethods,
  validateCoupon, subscribeToPlan, upgradePlan, cancelSubscription,
  clearAppliedCoupon, attachPaymentMethod, activateSubscription,
  previewPlanChange, fetchRetentionSettings, generateRetentionCoupon,
  fetchMyRetentionCoupon,
} from "../../../core/data/redux/slices/BillingSlice";
import { all_routes } from "../../router/all_routes";
import "./upgradePlan.css";
import { stripePromise } from "../../../core/config/stripeConfig";

const { Option } = Select;

const PERIOD_LABELS = { monthly: "Monthly", quarterly: "Quarterly", semiannual: "6 Months", yearly: "Yearly" };

// ── Price helpers ─────────────────────────────────────────────────────────────
// extraAgents = seats BEYOND the 1 that's always included (default 0)
const getEffectivePrice = (plan, period, extraAgents = 0) => {
  const tier = plan?.pricing?.[period];
  if (!tier) return 0;
  const agentPrice = plan?.agentPrice || 0;
  // Period discount applies to base plan only; agent seats at full monthly rate
  const basePerMonth = (tier.price || 0) * (1 - (tier.discountPercent || 0) / 100);
  return basePerMonth + extraAgents * agentPrice;
};

const getBilledAmount = (plan, period, extraAgents = 0) => {
  const pricePerMonth = getEffectivePrice(plan, period, extraAgents);
  if (period === "monthly") return pricePerMonth;
  if (period === "quarterly") return pricePerMonth * 3;
  if (period === "semiannual") return pricePerMonth * 6;
  return pricePerMonth * 12;
};

// Upgrade price includes the period discount because discountPercent encodes the actual
// billing-period rate (e.g. yearly tier stores price=$271 with discountPercent=89.18%
// to represent $29.25/mo — omitting the discount would show $3,252 instead of $351).
const getUpgradePrice = (plan, period) => {
  const tier = plan?.pricing?.[period];
  if (!tier) return 0;
  const discount = tier.discountPercent || 0;
  return (tier.price || 0) * (1 - discount / 100);
};
const getUpgradeBilledAmount = (plan, period) => {
  const p = getUpgradePrice(plan, period);
  if (period === "monthly") return p;
  if (period === "quarterly") return p * 3;
  if (period === "semiannual") return p * 6;
  return p * 12;
};

// ─────────────────────────────────────────────────────────────────────────────







export default function UpgradePlan() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const route = all_routes;

  const BILLING_TTL = 5 * 60 * 1000; // 5 minutes
  const {
    plans: billingPlans, plansLoading, plansLastFetched,
    subscription, planStatus, trialEndsAt,
    subscriptionLastFetched,
    paymentMethods: rawPaymentMethods,
    paymentMethodsLastFetched,
    appliedCoupon, couponLoading,
    actionLoading,
    upgradePreview, upgradePreviewLoading,
    retentionDiscountPercent, retentionCoupon, retentionLoading, myRetentionCoupon, retentionLifecycle,
    retentionSettingsLastFetched, myRetentionCouponLastFetched,
  } = useSelector((state) => state.billing);
  const plans = billingPlans || [];
  const paymentMethods = rawPaymentMethods || [];

  const [isInitialLoad, setIsInitialLoad] = useState(plans.length === 0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // billingCycle drives the Monthly / Yearly toggle — default Yearly
  const [billingCycle, setBillingCycle] = useState("annually");
  // billingPeriod is derived from billingCycle and used everywhere else
  const billingPeriod = billingCycle === "annually" ? "yearly" : "monthly";
  const [cancelLoading, setCancelLoading] = useState(false);
  const [autoRenewLoading, setAutoRenewLoading] = useState(false);

  const handleToggleAutoRenew = async (checked) => {
    setAutoRenewLoading(true);
    try {
      await import("../../../core/axios/axiosInstance").then(({ default: api }) =>
        api.post("/billing/toggle-auto-renewal", { autoRenew: checked })
      );
      dispatch(fetchCurrentSubscription());
    } catch (e) {
      console.error("Toggle auto-renewal failed", e);
    } finally {
      setAutoRenewLoading(false);
    }
  };

  // Retention cancel modal
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelStep, setCancelStep] = useState(1); // 1 = "don't go", 2 = "special offer"
  const [retentionAvailed, setRetentionAvailed] = useState(false);
  const [subscribeModal, setSubscribeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [agentCount, setAgentCount] = useState(1);
  const [couponInput, setCouponInput] = useState("");
  const [upgradeCouponInput, setUpgradeCouponInput] = useState(""); // for hasActiveSub modal
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [cardMode, setCardMode] = useState("existing");
  const [stripe, setStripe] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
const [elements, setElements] = useState(null); 
  // Initialize Stripe + Elements once
  useEffect(() => {
    stripePromise.then((stripeInstance) => {
      if (!stripeInstance) return;
      setStripe(stripeInstance);
    });
  }, []);

  // Bootstrap tooltip init — runs after plans render
  useEffect(() => {
    if (typeof window === "undefined" || !window.bootstrap?.Tooltip) return;
    const tooltipEls = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const instances = [];
    tooltipEls.forEach((el) => {
      // Dispose any existing instance before re-creating
      const existing = window.bootstrap.Tooltip.getInstance(el);
      if (existing) existing.dispose();
      const instance = new window.bootstrap.Tooltip(el, {
        trigger: "hover focus",
        container: "body", // render on body to escape overflow:hidden parent
        boundary: "window",
      });
      instances.push(instance);
    });
    return () => instances.forEach((inst) => inst.dispose());
  }, [plans]); // re-run when plans load

  useEffect(() => {
    const promises = [];
    if (!plansLastFetched) promises.push(dispatch(fetchActivePlans()));
    if (!subscriptionLastFetched) promises.push(dispatch(fetchCurrentSubscription()));
    if (!retentionSettingsLastFetched) promises.push(dispatch(fetchRetentionSettings()));
    if (!myRetentionCouponLastFetched) promises.push(dispatch(fetchMyRetentionCoupon()));
    const now = Date.now();
    if (!paymentMethodsLastFetched || now - paymentMethodsLastFetched > BILLING_TTL) promises.push(dispatch(fetchPaymentMethods()));
    if (promises.length > 0) {
      Promise.all(promises).finally(() => setIsInitialLoad(false));
    } else {
      setIsInitialLoad(false);
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    setIsRefreshing(true);
    Promise.all([
      dispatch(fetchActivePlans({ forceRefresh: true })),
      dispatch(fetchCurrentSubscription({ forceRefresh: true })),
      dispatch(fetchRetentionSettings({ forceRefresh: true })),
      dispatch(fetchMyRetentionCoupon({ forceRefresh: true })),
      dispatch(fetchPaymentMethods({ forceRefresh: true })),
    ]).finally(() => setIsRefreshing(false));
  };

  // useEffect(() => {
  //   if (paymentMethods.length > 0 && !selectedPaymentMethod) {
  //     setSelectedPaymentMethod(paymentMethods[0].id);
  //   }
  //   if (paymentMethods.length === 0) setCardMode("new");
  // }, [paymentMethods]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
  if (paymentMethods.length > 0) {
    if (!selectedPaymentMethod) {
      setSelectedPaymentMethod(paymentMethods[0].id);
    }
    setCardMode("existing"); // ✅ force existing card
  } else {
    setCardMode("new");
  }
}, [paymentMethods]);

  const needsNewCard = cardMode === "new" || paymentMethods.length === 0;

  // Mount card element whenever modal is open — destroy fully on close so it recreates cleanly
  useEffect(() => {
    if (!subscribeModal) {
      // Destroy (not just unmount) so Stripe releases the element and it can be recreated
      if (cardElement) {
        try { cardElement.destroy(); } catch (_) {}
        setCardElement(null);
      }
      return;
    }

    // Modal just opened — create a fresh elements instance and card each time
    if (!stripe) return;
    const freshElements = stripe.elements();
    setElements(freshElements);

    const instance = freshElements.create("card", {
      style: {
        base: {
          fontSize: "16px",
          color: "#374151",
          "::placeholder": { color: "#9ca3af" },
        },
        invalid: { color: "#ef4444" },
      },
      hidePostalCode: true,
    });

    // DOM may not be ready on the same tick — wait one frame
    const mountTimer = setTimeout(() => {
      const container = document.getElementById("subscribe-card-element");
      if (container) {
        container.innerHTML = ""; // clear any stale content
        instance.mount("#subscribe-card-element");
        instance.on("change", (e) => setCardError(e.error?.message || null));
        setCardElement(instance);
      }
    }, 50);

    return () => clearTimeout(mountTimer);
  }, [subscribeModal, stripe]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCardModeChange = (val) => {
    setCardMode(val);
    setCardError(null);
  };

  // ── Current plan detection ────────────────────────────────────────────────
  const currentPlanId = subscription?.planId?._id || subscription?.planId;
  const currentBillingPeriod = subscription?.billingPeriod || null;
  const isCurrentPlan = (plan) => plan._id === currentPlanId;
  const isCurrentPeriod = (period) => currentBillingPeriod === period;
  const hasActiveSub = planStatus === "active" && subscription && !subscription.cancelAtPeriodEnd;

  // Fetch prorated preview whenever an existing subscriber opens the modal or changes billing period.
  // Must be after hasActiveSub is defined to avoid TDZ error.
  useEffect(() => {
    if (!subscribeModal || !hasActiveSub || !selectedPlan) return;
    dispatch(previewPlanChange({ planId: selectedPlan._id, billingPeriod, couponCode: upgradeCouponInput || undefined }));
  }, [subscribeModal, selectedPlan?._id, billingPeriod, hasActiveSub, upgradeCouponInput, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Subscribe or Upgrade ──────────────────────────────────────────────────
  const openSubscribeModal = (plan) => {
    setSelectedPlan(plan);
    setAgentCount(0);
    setCouponInput("");
    dispatch(clearAppliedCoupon());
    setCardError(null);
    // billingPeriod is derived from billingCycle, no need to set it here
    // For existing subscribers: auto-fill their retention coupon if available
    if (planStatus === "active" && subscription && !subscription.cancelAtPeriodEnd) {
      setUpgradeCouponInput(myRetentionCoupon?.code || "");
    } else {
      setUpgradeCouponInput("");
    }
    setSubscribeModal(true);
  };

  const closeSubscribeModal = () => {
    setCardError(null);
    setSubscribeModal(false);
    setUpgradeCouponInput("");
    dispatch(clearAppliedCoupon());
  };

  const openCancelModal = () => {
    setCancelStep(1);
    setRetentionAvailed(false);
    setCancelModal(true);
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await dispatch(cancelSubscription()).unwrap();
      dispatch(fetchCurrentSubscription());
      setCancelModal(false);
    } catch (_) {}
    finally { setCancelLoading(false); }
  };

  const handleAvailRetentionOffer = async () => {
    try {
      await dispatch(generateRetentionCoupon()).unwrap();
      setRetentionAvailed(true);
    } catch (_) {
      // Error toast is already shown by the thunk — do NOT fake success here
    }
  };

  const handleValidateCoupon = () => {
    if (!couponInput.trim()) return;
    dispatch(validateCoupon(couponInput.trim().toUpperCase()));
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    setSubmitting(true);
    setCardError(null);

    try {
      let pmId = selectedPaymentMethod;

      // If adding a new card, create payment method directly
      if (cardMode === "new" || paymentMethods.length === 0) {
        if (!cardElement || !stripe) {
          setCardError("Card form is not ready. Please wait a moment and try again.");
          setSubmitting(false);
          return;
        }
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });
        if (error) { setCardError(error.message); setSubmitting(false); return; }

        // Attach the new card so it's saved for future use
        await dispatch(attachPaymentMethod({
          paymentMethodId: paymentMethod.id,
          setAsDefault: paymentMethods.length === 0,
        })).unwrap();
        pmId = paymentMethod.id;
      }

      if (!pmId) {
        setCardError("Please add a payment method to continue.");
        setSubmitting(false);
        return;
      }

      const payload = {
        planId: selectedPlan._id,
        billingPeriod,
        paymentMethodId: pmId,
        couponCode: appliedCoupon?.code || undefined,
        agentCount: agentCount + 1, // +1 for the always-included base seat
      };

      if (hasActiveSub) {
        await dispatch(upgradePlan({ planId: selectedPlan._id, billingPeriod, couponCode: upgradeCouponInput || undefined })).unwrap();
        // Always re-fetch subscription after upgrade so period end dates are fresh
        dispatch(fetchCurrentSubscription());
      } else {
        const result = await dispatch(subscribeToPlan(payload)).unwrap();
        // Stripe creates subscription as "incomplete" — must confirm the PaymentIntent
        if (result.clientSecret && stripe) {
          const { error: confirmError } = await stripe.confirmCardPayment(result.clientSecret, {
            payment_method: pmId,
          });
          if (confirmError) {
            setCardError(confirmError.message || "Payment confirmation failed.");
            setSubmitting(false);
            return;
          }
          // Tell backend to verify Stripe and mark subscription active
          await dispatch(activateSubscription()).unwrap();
        }
      }

      setSubscribeModal(false);
      dispatch(fetchCurrentSubscription());
      dispatch(fetchPaymentMethods());
    } catch (err) {
      setCardError(err?.message || "Payment failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Discount display ──────────────────────────────────────────────────────
  const calcDiscount = (plan) => {
    if (!appliedCoupon || !selectedPlan) return 0;
    const base = getBilledAmount(plan, billingPeriod, agentCount); // agentCount = extra agents
    if (appliedCoupon.discountType === "percentage") return (base * appliedCoupon.discountValue) / 100;
    return appliedCoupon.discountValue;
  };

  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Collect all billing periods that have pricing configured across all plans
  const allPeriodOrder = ["monthly", "quarterly", "semiannual", "yearly"];
  const activePeriods = [...new Set(
    plans.flatMap((p) => allPeriodOrder.filter((period) => (p.pricing?.[period]?.price || 0) > 0))
  )].sort((a, b) => allPeriodOrder.indexOf(a) - allPeriodOrder.indexOf(b));

  // ── Plan card renderer — one card per plan ───────────────────────────────
  const renderPlanCard = (plan) => {
    const VOYCELL = "#3C2371";
    // isCurrent only when BOTH plan AND billing period match subscription
    const isCurrent = isCurrentPlan(plan) && isCurrentPeriod(billingPeriod);
    const isPopular = plan.tag?.toLowerCase().includes("popular");

    const cardPeriod = plan.isEnterprise
      ? "monthly"
      : (plan.pricing?.[billingPeriod]?.price || 0) > 0
        ? billingPeriod
        : "monthly";

    const basePrice   = plan.pricing?.[cardPeriod]?.price || 0;
    const monthlyPrice = plan.pricing?.monthly?.price || 0;
    const discountPct  = hasActiveSub ? 0 : (plan.pricing?.[cardPeriod]?.discountPercent || 0);
    const discountedPricePerMonth = basePrice * (1 - discountPct / 100);
    const months      = { monthly: 1, quarterly: 3, semiannual: 6, yearly: 12 }[cardPeriod] || 1;
    const totalBilled = discountedPricePerMonth * months;
    const annualSavings = cardPeriod === "yearly" && monthlyPrice > 0
      ? Math.round(((monthlyPrice * 12 - totalBilled) / (monthlyPrice * 12)) * 100)
      : 0;
    const tagBg = plan.tagColor || "#7c3aed";

    return (
      <div
        key={plan._id}
        style={{
          background: "#fff",
          borderRadius: 20,
          border: `2px solid ${isCurrent ? VOYCELL : isPopular ? tagBg : "#e5e7eb"}`,
          boxShadow: isCurrent
            ? "0 12px 40px rgba(60,35,113,0.18)"
            : isPopular ? "0 12px 40px rgba(99,102,241,0.15)" : "0 2px 12px rgba(0,0,0,0.06)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = isCurrent ? "0 20px 50px rgba(60,35,113,0.22)" : "0 8px 28px rgba(0,0,0,0.12)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = isCurrent ? "0 12px 40px rgba(60,35,113,0.18)" : isPopular ? "0 12px 40px rgba(99,102,241,0.15)" : "0 2px 12px rgba(0,0,0,0.06)"; }}
      >
        {/* ── Corner ribbon "Your Plan" ── */}
        {isCurrent && (
          <div style={{
            position: "absolute", top: 18, right: -28,
            width: 120, textAlign: "center",
            background: VOYCELL, color: "#fff",
            fontSize: 10, fontWeight: 800, letterSpacing: "0.08em",
            textTransform: "uppercase", padding: "5px 0",
            transform: "rotate(45deg)",
            boxShadow: "0 2px 8px rgba(60,35,113,0.35)",
          }}>
            Your Plan
          </div>
        )}

        {/* ── Top banner for plan tag ── */}
        {plan.tag && (
          <div style={{
            background: tagBg, color: "#fff", textAlign: "center", padding: "8px 0",
            fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase",
          }}>
            {plan.tag}
          </div>
        )}

        <div style={{ padding: "28px 28px 22px", flex: 1, display: "flex", flexDirection: "column" }}>
          <h3 style={{ fontWeight: 800, fontSize: 22, margin: "0 0 6px", color: isCurrent ? VOYCELL : "#111827" }}>
            {plan.name}
          </h3>
          <p style={{ color: "#9ca3af", fontSize: 13, margin: "0 0 22px", lineHeight: 1.5 }}>{plan.description}</p>

          {/* ── Pricing ── */}
          {plan.isEnterprise ? (
            <div style={{ marginBottom: 20 }} />
          ) : hasActiveSub ? (
            (() => {
              const annualDiscPct = plan.pricing?.[cardPeriod]?.discountPercent || 0;
              const displayPrice  = cardPeriod === "yearly"
                ? basePrice * (1 - annualDiscPct / 100)
                : basePrice;
              return (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                    <span style={{ fontSize: 42, fontWeight: 800, color: "#111827", lineHeight: 1 }}>
                      ${displayPrice.toFixed(2)}
                    </span>
                    <span style={{ color: "#9ca3af", fontSize: 15 }}>/mo</span>
                  </div>
                </div>
              );
            })()
          ) : (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                    <span style={{ fontSize: 42, fontWeight: 800, color: "#111827", lineHeight: 1 }}>
                      ${(cardPeriod === "yearly" ? totalBilled / 12 : discountedPricePerMonth).toFixed(2)}
                    </span>
                    <span style={{ color: "#9ca3af", fontSize: 15 }}>/mo</span>
                  </div>
                </div>
                {annualSavings > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#059669", background: "#dcfce7", padding: "4px 10px", borderRadius: 20, marginTop: 6, alignSelf: "flex-start" }}>
                    Save {annualSavings}%
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ── Billed info chip ── */}
          {!plan.isEnterprise && basePrice > 0 && (
            <div style={{ marginBottom: 18 }}>
              {(() => {
                const yearlySaving = monthlyPrice > 0 ? monthlyPrice * 12 - totalBilled : 0;
                const showSaving   = cardPeriod === "yearly" && yearlySaving > 0;
                return (
                  <span style={{
                    display: "inline-block",
                    background: cardPeriod === "yearly" ? "#f0fdf4" : "#f9fafb",
                    color: cardPeriod === "yearly" ? "#059669" : "#6b7280",
                    border: `1px solid ${cardPeriod === "yearly" ? "#bbf7d0" : "#e5e7eb"}`,
                    borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 600,
                  }}>
                    {cardPeriod === "yearly" ? "Billed annually" : "Billed monthly"}
                  </span>
                );
              })()}
            </div>
          )}

          {/* ── Action button ── */}
          {plan.isEnterprise ? (
            <Button block style={{ borderRadius: 10, height: 46, fontWeight: 700, fontSize: 14, borderColor: "#0f172a", color: "#0f172a" }} href="mailto:sales@voycell.com">
              Contact Us
            </Button>
          ) : isCurrent && hasActiveSub ? (
            <Button danger block loading={cancelLoading} style={{ borderRadius: 10, height: 46, fontWeight: 700, fontSize: 14 }} onClick={openCancelModal}>
              Cancel Subscription
            </Button>
          ) : isCurrent && !hasActiveSub ? (
            <Button block disabled style={{ borderRadius: 10, height: 46, fontSize: 14 }}>
              <CheckCircleOutlined /> Cancellation Scheduled
            </Button>
          ) : (
            <Button
              block
              style={{ borderRadius: 10, height: 46, fontWeight: 700, fontSize: 14, background: VOYCELL, color: "#fff", border: "none", boxShadow: "0 4px 14px rgba(60,35,113,0.25)" }}
              onClick={() => openSubscribeModal(plan)}
            >
              {hasActiveSub ? "Switch to this plan" : "Get Started"}
            </Button>
          )}

          <Divider style={{ margin: "22px 0 16px", borderColor: "#f3f4f6" }} />

          {/* ── Features ── */}
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {[...(plan.features || []), ...(plan.commonFeatures || [])].map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, fontSize: 14, color: "#374151" }}>
                <CheckCircleOutlined style={{ color: VOYCELL, marginTop: 2, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{f.text}</span>
                {f.description && (
                  <span
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={f.description}
                    style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", color: "#9ca3af", flexShrink: 0, marginTop: 2 }}
                  >
                    <InfoCircleOutlined />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <div style={{ textAlign: "center", marginBottom: 32, position: "relative" }}>
          <Button
            icon={<ReloadOutlined />}
            loading={isRefreshing}
            onClick={handleRefresh}
            size="small"
            style={{ position: "absolute", right: 0, top: 0 }}
          >
            Refresh
          </Button>
          <h2 style={{ fontWeight: 700, fontSize: 28, margin: "0 0 8px" }}>Choose Your Plan</h2>
          <p style={{ color: "#6b7280", fontSize: 16 }}>
            {planStatus === "trial"
              ? `You have ${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""} left in your free trial.`
              : "Upgrade to unlock full access to all CRM features."}
          </p>
        </div>

        {/* ── Monthly / Yearly toggle ───────────────────────────────────── */}
        {(() => {
          const VOYCELL = "#3C2371";
          const eligible = plans.filter((p) => !p.isEnterprise && (p.pricing?.monthly?.price || 0) > 0 && (p.pricing?.yearly?.price || 0) > 0);
          const bestPlan = eligible.length ? eligible.reduce((acc, p) => (p.pricing?.yearly?.discountPercent || 0) > (acc.pricing?.yearly?.discountPercent || 0) ? p : acc, eligible[0]) : null;
          const maxDiscPct = bestPlan?.pricing?.yearly?.discountPercent || 0;
          const savingsMonths = (() => {
            if (!bestPlan) return null;
            const monthly = bestPlan.pricing.monthly.price;
            const yearlyTotal = bestPlan.pricing.yearly.price * 12 * (1 - maxDiscPct / 100);
            const months = Math.round((monthly * 12 - yearlyTotal) / monthly);
            return months > 0 ? months : null;
          })();

          return (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 36 }}>
              <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ display: "inline-flex", alignItems: "center", background: "rgba(60,35,113,0.07)", border: "1px solid rgba(60,35,113,0.18)", borderRadius: 999, padding: 4, gap: 2 }}>
                  <button onClick={() => setBillingCycle("monthly")} style={{ padding: "9px 28px", border: "none", borderRadius: 999, fontWeight: 600, fontSize: 14, cursor: "pointer", transition: "all 0.18s", background: billingCycle === "monthly" ? VOYCELL : "transparent", color: billingCycle === "monthly" ? "#fff" : "rgba(60,35,113,0.65)", boxShadow: billingCycle === "monthly" ? "0 2px 10px rgba(60,35,113,0.3)" : "none" }}>Monthly</button>
                  <button onClick={() => setBillingCycle("annually")} style={{ padding: "9px 28px", border: "none", borderRadius: 999, fontWeight: 600, fontSize: 14, cursor: "pointer", transition: "all 0.18s", background: billingCycle === "annually" ? VOYCELL : "transparent", color: billingCycle === "annually" ? "#fff" : "rgba(60,35,113,0.65)", boxShadow: billingCycle === "annually" ? "0 2px 10px rgba(60,35,113,0.3)" : "none", display: "flex", alignItems: "center", gap: 6 }}>
                    Yearly
                    {maxDiscPct > 0 && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: billingCycle === "annually" ? "rgba(255,255,255,0.22)" : "rgba(60,35,113,0.12)", color: billingCycle === "annually" ? "#fff" : VOYCELL, transition: "all 0.18s" }}>{maxDiscPct}% off</span>}
                  </button>
                </div>
                {savingsMonths && (
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 4, alignSelf: "flex-end", transform: "translateX(80px)", pointerEvents: "none" }}>
                    <svg width="32" height="26" viewBox="0 0 32 26" fill="none">
                      <path d="M 26 22 C 20 22, 10 16, 6 4" stroke="#e07b39" strokeWidth="2" strokeLinecap="round" />
                      <path d="M 6 4 L 12 10 M 6 4 L 3 11" stroke="#e07b39" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ color: "#e07b39", fontWeight: 700, fontSize: 13, fontStyle: "italic", whiteSpace: "nowrap" }}>
                      Save {savingsMonths} month{savingsMonths > 1 ? "s'" : "'"} cost
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Cards — all plans + enterprise in one row ─────────────────── */}
        {isInitialLoad ? (
          <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24, maxWidth: 1300, margin: "0 auto" }}>
            {plans.filter((p) => !p.isEnterprise).map(renderPlanCard)}
            {plans.filter((p) => p.isEnterprise).length > 0
              ? plans.filter((p) => p.isEnterprise).map(renderPlanCard)
              : (
                <div style={{ background: "#fff", borderRadius: 20, padding: "32px 28px 24px", border: "2px solid #0f172a", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column" }}>
                  <CrownOutlined style={{ fontSize: 32, color: "#0f172a", marginBottom: 12 }} />
                  <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 6 }}>Enterprise</h3>
                  <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20, flex: 1 }}>Custom pricing for large teams. Unlimited agents, dedicated support & SLA.</p>
                  <div style={{ marginBottom: 20 }} />
                  <Button block style={{ borderRadius: 10, height: 46, fontWeight: 700, borderColor: "#0f172a", color: "#0f172a" }} href="mailto:sales@voycell.com">Contact Us</Button>
                </div>
              )}
          </div>
        )}

        {/* View Billing */}
        <div style={{ textAlign: "center", marginTop: 32 }}>
          <Button type="link" icon={<CreditCardOutlined />} onClick={() => navigate(route.billing)}>
            View Billing History & Manage Cards
          </Button>
        </div>

        {/* ── Active subscription info bar ─────────────────────────────────── */}
        {/* {subscription && planStatus === "active" && (
          <div style={{
            maxWidth: 640, margin: "20px auto 0", background: "#fff",
            borderRadius: 12, border: "1px solid #e5e7eb",
            padding: "16px 20px", display: "flex", alignItems: "center",
            justifyContent: "space-between", flexWrap: "wrap", gap: 16,
            boxShadow: "0 1px 6px rgba(0,0,0,.06)",
          }}>
            <div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 2 }}>
                {subscription.cancelAtPeriodEnd ? "Plan expires on" : "Next renewal"}
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>
                {subscription.currentPeriodEnd ? dayjs(subscription.currentPeriodEnd).format("DD MMM YYYY") : "—"}
              </div>
              {subscription.cancelAtPeriodEnd && (
                <Tag color="orange" style={{ marginTop: 4, fontSize: 11 }}>Will not renew — cancels on this date</Tag>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>Auto-renewal</span>
              <Tooltip title={subscription.cancelAtPeriodEnd ? "Enable to keep your plan active after the current period" : "Disable to cancel at the end of the current period"}>
                <Switch checked={!subscription.cancelAtPeriodEnd} loading={autoRenewLoading} onChange={handleToggleAutoRenew} checkedChildren="ON" unCheckedChildren="OFF" />
              </Tooltip>
            </div>
          </div>
        )} */}

        {/* ── Retention Cancel Modal ───────────────────────────────────────── */}
        <Modal
          open={cancelModal}
          onCancel={() => setCancelModal(false)}
          footer={null}
          width={460}
          centered
          maskClosable={false}
        >
          {/* Step 1 — Don't go */}
          {cancelStep === 1 && (
            <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>😢</div>
              <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 8, color: "#111827" }}>
                We don't want to lose you!
              </h3>
              <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>
                Cancelling means you'll lose access to all your CRM data and features at the end of your billing period.
                Are you sure you want to continue?
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Button
                  type="primary"
                  size="large"
                  block
                  style={{ background: "#6366f1", height: 44, fontWeight: 600, borderRadius: 8 }}
                  onClick={() => setCancelModal(false)}
                >
                  Keep My Plan
                </Button>
                <Button
                  size="large"
                  block
                  loading={cancelLoading}
                  style={{ height: 44, borderRadius: 8, color: "#6b7280" }}
                  onClick={() => (retentionLifecycle?.hasGenerated || myRetentionCoupon) ? handleCancelSubscription() : setCancelStep(2)}
                >
                  Continue to Cancel →
                </Button>
              </div>
            </div>
          )}

          {/* Step 2 — Special offer */}
          {cancelStep === 2 && !retentionAvailed && !retentionLifecycle?.hasGenerated && (
            <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
              <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 8, color: "#111827" }}>
                Wait — here's a special offer!
              </h3>
              <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                Stay with us and get{" "}
                <span style={{ fontWeight: 700, color: "#6366f1", fontSize: 18 }}>
                  {retentionDiscountPercent}% off
                </span>{" "}
                your next billing cycle — automatically applied, no strings attached.
              </p>
              <div style={{
                background: "#f5f3ff",
                borderRadius: 12,
                padding: "16px 20px",
                marginBottom: 24,
                border: "2px dashed #a5b4fc",
              }}>
                <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 4 }}>Your exclusive discount coupon</div>
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "0.15em", color: "#6366f1" }}>
                  Generated on click
                </div>
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>
                  1-time use · auto-applied on next renewal
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Button
                  type="primary"
                  size="large"
                  block
                  loading={retentionLoading}
                  style={{ background: "#6366f1", height: 48, fontWeight: 700, borderRadius: 8, fontSize: 15 }}
                  onClick={handleAvailRetentionOffer}
                >
                  🎉 Get {retentionDiscountPercent}% Off My Next Bill
                </Button>
                <Button
                  size="large"
                  block
                  danger
                  loading={cancelLoading}
                  style={{ height: 44, borderRadius: 8 }}
                  onClick={handleCancelSubscription}
                >
                  No thanks, cancel my subscription
                </Button>
              </div>
            </div>
          )}

          {/* Step 2 — Offer availed */}
          {cancelStep === 2 && retentionAvailed && (
            <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🥳</div>
              <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 8, color: "#059669" }}>
                Your discount is confirmed!
              </h3>
              {retentionCoupon && (
                <div style={{
                  background: "#ecfdf5",
                  borderRadius: 12,
                  padding: "16px 20px",
                  marginBottom: 20,
                  border: "2px solid #6ee7b7",
                  display: "inline-block",
                  minWidth: 200,
                }}>
                  <div style={{ color: "#065f46", fontSize: 12, marginBottom: 4 }}>Your coupon code</div>
                  <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "0.15em", color: "#059669" }}>
                    {retentionCoupon.code}
                  </div>
                  <div style={{ color: "#065f46", fontSize: 12, marginTop: 4 }}>
                    {retentionCoupon.discountValue}% off · auto-applied on next renewal
                  </div>
                </div>
              )}
              <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
                Your {retentionDiscountPercent}% discount will be automatically applied on your next billing cycle.
                No action needed — just keep enjoying the platform!
              </p>
              <Button
                type="primary"
                size="large"
                block
                style={{ background: "#059669", height: 44, fontWeight: 600, borderRadius: 8 }}
                onClick={() => setCancelModal(false)}
              >
                Great, keep my subscription!
              </Button>
            </div>
          )}
        </Modal>

        {/* ── Subscribe / Upgrade Modal ─────────────────────────────────── */}
        <Modal
          title={
            <Space>
              <CrownOutlined style={{ color: "#6366f1" }} />
              {hasActiveSub ? "Change Plan" : "Complete Subscription"}
            </Space>
          }
          open={subscribeModal}
          onCancel={closeSubscribeModal}
          footer={null}
          width={520}
        >
          {selectedPlan && (
            <div>
              {/* Plan Summary */}
              <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{selectedPlan.name}</div>
                    {/* Billing period label only — no tab switcher in the modal */}
                    <div style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>
                      {billingCycle === "annually" ? "Billed annually" : "Billed monthly"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {(() => {
                      // Per-month price with annual discount applied (matches card display)
                      const discPct = selectedPlan.pricing?.[billingPeriod]?.discountPercent || 0;
                      const basePerMonth = selectedPlan.pricing?.[billingPeriod]?.price || 0;
                      const discountedPerMonth = basePerMonth * (1 - discPct / 100);
                      const yearlyTotal = discountedPerMonth * 12;

                      return (
                        <>
                          <div style={{ fontWeight: 700, fontSize: 20, color: "#6366f1" }}>
                            ${discountedPerMonth.toFixed(2)}/mo
                          </div>
                          {billingPeriod === "yearly" && (
                            <div style={{ color: "#6b7280", fontSize: 12 }}>
                              ${yearlyTotal.toFixed(2)} billed yearly
                            </div>
                          )}
                          {appliedCoupon && (
                            <div style={{ color: "#7c3aed", fontSize: 12 }}>
                              Coupon applied
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Agent count selector — only for new subscriptions */}
                {!hasActiveSub && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #ddd4fe" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: 500, fontSize: 13 }}>Additional Agents</span>
                        <div style={{ color: "#6b7280", fontSize: 12 }}>1 license included in plan</div>
                        {(selectedPlan.agentPrice || 0) > 0 && (
                          <div style={{ color: "#7c3aed", fontSize: 12 }}>
                            +${selectedPlan.agentPrice}/extra agent/mo
                          </div>
                        )}
                      </div>
                      <InputNumber
                        min={0}
                        max={499}
                        value={agentCount}
                        onChange={(v) => setAgentCount(v ?? 0)}
                        style={{ width: 80 }}
                        size="small"
                      />
                    </div>
                    {(selectedPlan.agentPrice || 0) > 0 && agentCount > 0 && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                        Base ${selectedPlan.pricing?.[billingPeriod]?.price?.toFixed(2)}/mo
                        {" + "}
                        {agentCount} extra agent{agentCount !== 1 ? "s" : ""} × ${selectedPlan.agentPrice}/mo
                        {" = "}
                        <strong style={{ color: "#6366f1" }}>
                          ${getEffectivePrice(selectedPlan, billingPeriod, agentCount).toFixed(2)}/mo
                        </strong>
                      </div>
                    )}
                  </div>
                )}

                {!hasActiveSub && appliedCoupon && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #ddd4fe", display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#7c3aed" }}>Coupon: {appliedCoupon.code}</span>
                    <span style={{ color: "#7c3aed", fontWeight: 600 }}>-${calcDiscount(selectedPlan).toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Coupon — first-time subscribers only */}
              {!hasActiveSub && (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontWeight: 500, marginBottom: 8, fontSize: 14 }}>
                    Promo Code
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Input
                      placeholder="Enter promo code"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      disabled={!!appliedCoupon}
                      style={{ textTransform: "uppercase" }}
                      onPressEnter={handleValidateCoupon}
                    />
                    {appliedCoupon ? (
                      <Button icon={<CloseOutlined />} onClick={() => { dispatch(clearAppliedCoupon()); setCouponInput(""); }}>
                        Remove
                      </Button>
                    ) : (
                      <Button onClick={handleValidateCoupon} loading={couponLoading}>Apply</Button>
                    )}
                  </div>
                  {appliedCoupon && (
                    <Tag color="green" style={{ marginTop: 6 }}>
                      ✓ {appliedCoupon.discountType === "percentage" ? `${appliedCoupon.discountValue}% off` : `$${appliedCoupon.discountValue} off`} applied
                    </Tag>
                  )}
                </div>
              )}

              {/* Payment method — only for new subscriptions */}
              {!hasActiveSub && (
                <>
                  <Divider />
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontWeight: 500, marginBottom: 8, fontSize: 14 }}>
                      Payment Method
                    </label>

                    {paymentMethods.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <Select
                          style={{ width: "100%", marginBottom: 8 }}
                          value={cardMode}
                          onChange={handleCardModeChange}
                        >
                          <Option value="existing">Use saved card</Option>
                          <Option value="new">Add new card</Option>
                        </Select>

                        {cardMode === "existing" && (
                          <Select
                            style={{ width: "100%" }}
                            value={selectedPaymentMethod}
                            onChange={setSelectedPaymentMethod}
                          >
                            {paymentMethods.map((pm) => (
                              <Option key={pm.id} value={pm.id}>
                                {pm.card?.brand?.toUpperCase()} •••• {pm.card?.last4} &nbsp; exp {pm.card?.exp_month}/{pm.card?.exp_year}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </div>
                    )}

                    <div
                      id="subscribe-card-element"
                      style={{
                        display: needsNewCard ? "block" : "none",
                        padding: "12px 16px",
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        minHeight: 44,
                        background: "#fff",
                      }}
                    />
                  </div>

                  <p style={{ color: "#6b7280", fontSize: 12, marginBottom: 20 }}>
                    Your payment is secured by Stripe. We never store card details.
                  </p>
                </>
              )}

              {/* Coupon field — for existing subscribers changing plan */}
              {hasActiveSub && (
                <div style={{ marginBottom: 16 }}>
                  {/* If user has a retention coupon, show it as auto-applied */}
                  {myRetentionCoupon && upgradeCouponInput === myRetentionCoupon.code ? (
                    <div style={{
                      background: "#ecfdf5", borderRadius: 8, padding: "10px 14px",
                      border: "1px solid #6ee7b7", display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#065f46" }}>
                          🎁 Retention offer applied
                        </div>
                        <div style={{ fontSize: 12, color: "#059669", letterSpacing: "0.05em", fontWeight: 700, marginTop: 2 }}>
                          {myRetentionCoupon.code} — {myRetentionCoupon.discountValue}% off this payment
                        </div>
                      </div>
                      <Button
                        size="small"
                        type="text"
                        style={{ color: "#9ca3af", fontSize: 11 }}
                        icon={<CloseOutlined />}
                        onClick={() => setUpgradeCouponInput("")}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <label style={{ display: "block", fontWeight: 500, marginBottom: 6, fontSize: 13, color: "#374151" }}>
                        Promo / Coupon Code
                      </label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Input
                          placeholder="Enter coupon code"
                          value={upgradeCouponInput}
                          onChange={(e) => setUpgradeCouponInput(e.target.value.toUpperCase())}
                          style={{ textTransform: "uppercase" }}
                          onPressEnter={() => {
                            // trigger re-preview by re-setting state (effect depends on upgradeCouponInput)
                            setUpgradeCouponInput((v) => v.trim());
                          }}
                        />
                        {upgradeCouponInput && (
                          <Button icon={<CloseOutlined />} onClick={() => setUpgradeCouponInput("")} />
                        )}
                      </div>
                      {/* If they have a retention coupon but removed it, offer to re-apply */}
                      {myRetentionCoupon && !upgradeCouponInput && (
                        <div
                          style={{ marginTop: 6, fontSize: 12, color: "#6366f1", cursor: "pointer" }}
                          onClick={() => setUpgradeCouponInput(myRetentionCoupon.code)}
                        >
                          🎁 Apply your {myRetentionCoupon.discountValue}% retention offer ({myRetentionCoupon.code})
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Prorated breakdown — only for existing subscribers */}
              {hasActiveSub && (
                <div style={{ marginBottom: 20 }}>
                  <Divider style={{ margin: "0 0 16px" }} />
                  {upgradePreviewLoading ? (
                    <div style={{ textAlign: "center", padding: "12px 0", color: "#6b7280" }}>
                      <Spin size="small" style={{ marginRight: 8 }} />
                      Calculating charge…
                    </div>
                  ) : upgradePreview ? (
                    <div style={{ fontSize: 14 }}>
                      {/* Row 1: plan charge — broken down when extra agent seats are included */}
                      {upgradePreview.extraAgentCount > 0 ? (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ color: "#374151" }}>Base plan charge</span>
                            <span style={{ fontWeight: 600 }}>${(upgradePreview.basePlanTotal / 100).toFixed(2)}</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                            <span style={{ color: "#6b7280", fontSize: 12 }}>
                              {upgradePreview.extraAgentCount} extra agent seat{upgradePreview.extraAgentCount !== 1 ? "s" : ""} (carried from current plan)
                            </span>
                            <span style={{ color: "#6b7280", fontSize: 12 }}>+${(upgradePreview.agentSeatsTotal / 100).toFixed(2)}</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, paddingTop: 4, borderTop: "1px solid #f0e6ff" }}>
                            <span style={{ color: "#374151", fontWeight: 600 }}>New plan charge</span>
                            <span style={{ fontWeight: 700 }}>${(upgradePreview.planTotal / 100).toFixed(2)}</span>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ color: "#374151" }}>New plan charge</span>
                          <span style={{ fontWeight: 600 }}>${(upgradePreview.planTotal / 100).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Row 2: proration credit (comes before coupon — coupon applies to what's left) */}
                      {upgradePreview.proratedCredit > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ color: "#6b7280" }}>Credit (unused time on current plan)</span>
                          <span style={{ color: "#059669", fontWeight: 600 }}>-${(upgradePreview.proratedCredit / 100).toFixed(2)}</span>
                        </div>
                      )}
                      {/* Row 3: coupon applied to remaining balance */}
                      {upgradePreview.couponDiscount > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ color: "#7c3aed" }}>
                            Coupon ({upgradePreview.appliedCoupon?.discountValue}% off remaining)
                            {upgradePreview.appliedCoupon && (
                              <Tag color="purple" style={{ marginLeft: 6, fontSize: 11 }}>
                                {upgradePreview.appliedCoupon.code}
                              </Tag>
                            )}
                          </span>
                          <span style={{ color: "#7c3aed", fontWeight: 600 }}>-${(upgradePreview.couponDiscount / 100).toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{
                        display: "flex", justifyContent: "space-between",
                        paddingTop: 10, borderTop: "1px solid #e5e7eb", marginTop: 4,
                      }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>Charged today</span>
                        <span style={{ fontWeight: 800, fontSize: 16, color: "#6366f1" }}>
                          ${(upgradePreview.amountDueNow / 100).toFixed(2)}
                        </span>
                      </div>
                      {upgradePreview.amountDueNow === 0 && (upgradePreview.proratedCredit > 0 || upgradePreview.couponDiscount > 0) && (
                        <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                          {upgradePreview.proratedCredit > 0
                            ? "Your credit fully covers this switch."
                            : "Your coupon covers the full charge."}{" "}
                          Future bills at ${getUpgradeBilledAmount(selectedPlan, billingPeriod).toFixed(2)}.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                      <span style={{ fontWeight: 700 }}>Billed today</span>
                      <span style={{ fontWeight: 800, color: "#6366f1" }}>
                        ${getUpgradeBilledAmount(selectedPlan, billingPeriod).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Error message — always visible regardless of subscription state */}
              {cardError && (
                <Alert message={cardError} type="error" style={{ marginBottom: 16 }} showIcon />
              )}

              {(() => {
                // Determine the charge amount shown on the submit button
                let buttonLabel;
                if (hasActiveSub) {
                  const chargeNow = upgradePreview
                    ? (upgradePreview.amountDueNow / 100).toFixed(2)
                    : getUpgradeBilledAmount(selectedPlan, billingPeriod).toFixed(2);
                  const couponLabel = upgradePreview?.couponDiscount > 0 ? " (coupon applied)" : "";
                  buttonLabel = `Switch Plan — $${chargeNow} today${couponLabel}`;
                } else {
                  const total = getBilledAmount(selectedPlan, billingPeriod, agentCount);
                  const disc = calcDiscount(selectedPlan);
                  buttonLabel = `Subscribe — $${Math.max(0, total - disc).toFixed(2)}`;
                }
                return (
                  <Button
                    type="primary"
                    block
                    loading={submitting || actionLoading || upgradePreviewLoading}
                    onClick={handleSubscribe}
                    style={{ background: "#6366f1", height: 44, fontWeight: 600, fontSize: 16 }}
                  >
                    {buttonLabel}
                  </Button>
                );
              })()}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
